import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, ArrowLeft, ShieldCheck, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { authAPI, paymentsAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CASTELLI } from "../constants";
import { toast } from "sonner";

export default function RegisterBusiness() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [form, setForm] = useState({
    company_name: "",
    vat_number: "",
    contact_name: "",
    email: "",
    password: "",
    castello: "",
    address: "",
    phone: "",
    country: "SM",
  });
  const isIT = form.country === "IT";
  const subAmount = isIT ? "29,99" : "9,99";
  const listingAmount = isIT ? "1,00" : "0,50";
  const subPkgId = isIT ? "business_subscription_year_it" : "business_subscription_year";
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.vat_number || !form.contact_name || !form.email || !form.password || !form.castello) {
      return toast.error("Compila tutti i campi obbligatori");
    }
    if (!acceptedTerms) {
      return toast.error("Accetta Privacy e Termini per continuare");
    }
    setLoading(true);
    try {
      const { user, token } = await authAPI.registerBusiness({ ...form, accepted_terms: true });
      loginWithToken(user, token);
      toast.success(`Benvenuta, ${user.company_name || user.name}!`, {
        description: `Ora completa l'iscrizione annuale con pagamento Stripe (€${subAmount}/anno)`,
      });
      // Immediately start subscription checkout
      try {
        const origin = window.location.origin;
        const { url } = await paymentsAPI.createBusinessSubscription(origin, subPkgId);
        if (url) {
          window.location.href = url;
          return;
        }
      } catch (err) {
        toast.error("Account creato ma errore nel checkout. Vai sul profilo per completare il pagamento.");
      }
      navigate("/profilo");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore registrazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] bg-gradient-to-br from-[#1d3d3a]/5 via-white to-amber-50 flex items-center justify-center px-4 py-10">
      <div className="max-w-2xl w-full" data-testid="register-business-page">
        <Link to="/scegli-account" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#1d3d3a] mb-4">
          <ArrowLeft className="w-4 h-4" /> Indietro
        </Link>

        <div className="bg-white border-2 border-[#1d3d3a]/10 rounded-3xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-[#1d3d3a] to-[#244c48] text-white p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-400 text-[#1d3d3a] flex items-center justify-center">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl font-black">Registra la tua Azienda</h1>
                <p className="text-amber-50/80 text-sm">San Marino & Italia · Account Pro</p>
              </div>
            </div>

            {/* Country selector */}
            <div className="mt-4 grid grid-cols-2 gap-2" data-testid="business-country-selector">
              <CountryButton
                code="SM"
                flag="🇸🇲"
                label="San Marino"
                price="€9,99/anno · €0,50/annuncio"
                active={form.country === "SM"}
                onClick={() => update("country", "SM")}
              />
              <CountryButton
                code="IT"
                flag="🇮🇹"
                label="Italia"
                price="€29,99/anno · €1/annuncio mese"
                active={form.country === "IT"}
                onClick={() => update("country", "IT")}
              />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <PriceTag big={`€${subAmount}`} small="iscrizione / anno" />
              <PriceTag big={`€${listingAmount}`} small={isIT ? "per annuncio / mese" : "per annuncio"} />
            </div>
          </div>

          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Ragione sociale *" testId="business-company">
                <Input value={form.company_name} onChange={(e) => update("company_name", e.target.value)} placeholder="es. Bottega Sammarinese SRL" />
              </Field>
              <Field label="P.IVA / COE *" testId="business-vat">
                <Input value={form.vat_number} onChange={(e) => update("vat_number", e.target.value)} placeholder={isIT ? "IT01234567890" : "SM12345 o IT01234567890"} />
              </Field>
              <Field label="Referente (nome e cognome) *" testId="business-contact">
                <Input value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} placeholder="Mario Rossi" />
              </Field>
              <Field label={isIT ? "Città / Regione *" : "Castello *"} testId="business-castello">
                {isIT ? (
                  <Input value={form.castello} onChange={(e) => update("castello", e.target.value)} placeholder="es. Rimini, Emilia-Romagna" />
                ) : (
                  <Select value={form.castello} onValueChange={(v) => update("castello", v)}>
                    <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>
                      {CASTELLI.slice(1).map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Email aziendale *" testId="business-email">
                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="info@miaazienda.sm" />
              </Field>
              <Field label="Password *" testId="business-password">
                <Input type="password" value={form.password} onChange={(e) => update("password", e.target.value)} placeholder="min. 6 caratteri" />
              </Field>
              <Field label="Indirizzo (opzionale)" testId="business-address">
                <Input value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="Via, n., città" />
              </Field>
              <Field label="Telefono (opzionale)" testId="business-phone">
                <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+378 0549 ..." />
              </Field>
            </div>

            <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer select-none mt-2" data-testid="business-terms-row">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#e86a24]"
                data-testid="business-terms-checkbox"
              />
              <span>
                Confermo che la mia azienda opera in {isIT ? "Italia" : "Repubblica di San Marino"} e accetto la{" "}
                <Link to="/privacy" target="_blank" className="text-[#e86a24] font-semibold hover:underline">Privacy</Link>{" "}
                e i{" "}
                <Link to="/termini" target="_blank" className="text-[#e86a24] font-semibold hover:underline">Termini di Servizio</Link>.
              </span>
            </label>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                Al click sul bottone qui sotto verrai reindirizzato a <strong>Stripe</strong> per completare il pagamento dell'iscrizione annuale (<strong>€{subAmount}</strong>). L'account è creato subito, ma puoi pubblicare annunci solo dopo il pagamento.{isIT && <> Gli annunci delle aziende italiane mostrano un banner <strong>"Spedito da Italia — costo incluso di spedizione"</strong>.</>}
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !acceptedTerms}
              className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-black rounded-full text-base disabled:opacity-60"
              data-testid="business-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Building2 className="w-5 h-5 mr-2" />}
              {loading ? "Registrazione…" : `Registra e paga €${subAmount}/anno`}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <label className="text-xs font-bold text-slate-700 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function PriceTag({ big, small }) {
  return (
    <div className="bg-white/10 backdrop-blur rounded-xl px-3 py-2 border border-white/15">
      <div className="text-lg font-black text-amber-300">{big}</div>
      <div className="text-[10px] uppercase tracking-wider text-amber-100/70 font-bold mt-0.5">{small}</div>
    </div>
  );
}

function CountryButton({ code, flag, label, price, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`country-${code.toLowerCase()}-btn`}
      className={`text-left rounded-xl px-3 py-2.5 border-2 transition-all ${
        active
          ? "bg-amber-400 border-amber-300 text-[#1d3d3a]"
          : "bg-white/5 border-white/20 text-white hover:bg-white/10"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="text-xl">{flag}</span>
        <div className={`text-sm font-black ${active ? "text-[#1d3d3a]" : "text-white"}`}>{label}</div>
      </div>
      <div className={`text-[10px] font-bold mt-0.5 ${active ? "text-[#1d3d3a]/80" : "text-amber-100/70"}`}>{price}</div>
    </button>
  );
}
